/**
 * Bootstrap component that restores a saved session snapshot when the user
 * returns to `/home` after an external redirect.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { AppSessionSnapshot } from "../../services/sessionRestore/sessionRestore.types";
import { sessionRestoreService } from "../../services/sessionRestore/SessionRestoreService";
import {
    APP_SESSION_RESTORE_DEFAULT,
    appSessionRestoreSlice,
    ephemeralStore,
    mapReadySlice,
    servicesBootstrapSlice,
    useEphemeralSelector,
} from "../../store/ephemeral";
import { usePersistenceReady } from "../../store/persistence";
import { SessionRestoreLoadingOverlay } from "./SessionRestoreLoadingOverlay";

/** Maximum time the restore loader can remain visible before it is forced off. */
const RESTORE_TIMEOUT_MS = 10_000;

/**
 * Checks whether the current route is the home page (or the root redirect).
 *
 * @param pathname - Current location pathname.
 * @returns `true` when the restore should be attempted.
 */
function isHomeRoute( pathname: string ): boolean {
    const normalized = pathname.replace( /\/+$/, "" );

    return normalized === "/home" || normalized === "";
}

/** Props for {@link SessionRestoreBootstrap}. */
export interface SessionRestoreBootstrapProps {

    /** Whether the dark UI palette is active. */
    "isDarkUi": boolean;
}

/**
 * Reads a saved session snapshot when the user lands on `/home`, waits for
 * persistence, map and services to be ready, then exposes the snapshot to
 * registered contributors and shows a loader until every contributor is restored.
 *
 * @param props - Component props.
 * @returns The loading overlay when restoring, otherwise `null`.
 */
export const SessionRestoreBootstrap: React.FC<SessionRestoreBootstrapProps> = ( { isDarkUi } ) => {
    const location = useLocation();
    const persistenceReady = usePersistenceReady();
    const mapReady = useEphemeralSelector( mapReadySlice, ( state ) => state.isMapReady );
    const servicesReady = useEphemeralSelector( servicesBootstrapSlice, ( state ) => state.servicesReady );
    const isRestoring = useEphemeralSelector( appSessionRestoreSlice, ( state ) => state.isRestoring );
    const pendingSnapshot = useEphemeralSelector( appSessionRestoreSlice, ( state ) => state.pendingSnapshot );
    const restoredKeys = useEphemeralSelector( appSessionRestoreSlice, ( state ) => state.restoredKeys );

    const snapshotRef = useRef<AppSessionSnapshot | null>( null );
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null );
    const onHome = isHomeRoute( location.pathname );

    const completeRestore = useCallback( (): void => {
        if ( timeoutRef.current !== null ) {
            clearTimeout( timeoutRef.current );
            timeoutRef.current = null;
        }

        snapshotRef.current = null;

        /*
         * Keep the search-skip flag so the itinerary list effect can consume it once
         * the restored React state (departure/arrival/selectedItinerary) has flushed.
         * Record the completion time so map auto-fit effects can avoid overwriting the
         * restored camera immediately after it has been applied.
         */
        const { skipNextItinerarySearch } = ephemeralStore.peek( appSessionRestoreSlice );
        ephemeralStore.setValue( appSessionRestoreSlice, {
            ...APP_SESSION_RESTORE_DEFAULT,
            skipNextItinerarySearch,
            "mapRestoredAt": Date.now(),
        } );
        sessionRestoreService.clearSnapshot();
    }, [] );

    useEffect( () => {
        if ( !onHome ) {
            return;
        }

        if ( snapshotRef.current !== null ) {
            return;
        }

        const snapshot = sessionRestoreService.tryRestoreOnBoot();
        if ( snapshot === null ) {
            return;
        }

        snapshotRef.current = snapshot;

        ephemeralStore.setValue( appSessionRestoreSlice, ( previous ) => ( {
            ...previous,
            "isRestoring":     true,
            "pendingSnapshot": null,
            "restoredKeys":    [],
        } ) );
    }, [ onHome ] );

    useEffect( () => {
        if ( !onHome || !isRestoring || snapshotRef.current === null ) {
            return;
        }

        if ( pendingSnapshot !== null ) {
            return;
        }

        if ( !persistenceReady || !mapReady || !servicesReady ) {
            return;
        }

        ephemeralStore.setValue( appSessionRestoreSlice, ( previous ) => ( {
            ...previous,
            "pendingSnapshot": snapshotRef.current,
        } ) );
    }, [ onHome, isRestoring, pendingSnapshot, persistenceReady, mapReady, servicesReady ] );

    useEffect( () => {
        if ( !onHome || !isRestoring || pendingSnapshot === null ) {
            return;
        }

        const expectedKeys = Object.keys( pendingSnapshot ).filter( ( key ) => key !== "version" && key !== "savedAt" );

        const allRestored = expectedKeys.every( ( key ) => restoredKeys.includes( key ) );
        if ( allRestored ) {
            completeRestore();
        }
    }, [ onHome, isRestoring, pendingSnapshot, restoredKeys, completeRestore ] );

    useEffect( () => {
        if ( !onHome || !isRestoring ) {
            if ( timeoutRef.current !== null ) {
                clearTimeout( timeoutRef.current );
                timeoutRef.current = null;
            }

            return;
        }

        if ( timeoutRef.current !== null ) {
            return;
        }

        timeoutRef.current = setTimeout( () => {
            console.warn( "[SessionRestoreBootstrap] Restore timed out, forcing completion." );
            completeRestore();
        }, RESTORE_TIMEOUT_MS );

        return () => {
            if ( timeoutRef.current !== null ) {
                clearTimeout( timeoutRef.current );
                timeoutRef.current = null;
            }
        };
    }, [ onHome, isRestoring, completeRestore ] );

    if ( !onHome || !isRestoring ) {
        return null;
    }

    return <SessionRestoreLoadingOverlay isDarkUi={isDarkUi} />;
};
